<!--
  联系我们页
  用途：主功能二：收集采购咨询（姓名、公司、邮箱、需求），参考 Advanta 联系模块布局
  用法：路由 `/contact`；提交后调用 POST /api/inquiries 发信到联系邮箱
-->
<template>
  <div class="contact-page">
    <section class="contact-hero">
      <!--
        Hero 双栏布局（与首页一致）
        用途：左侧放标题文案，右侧空位放大展示红色 Logo
      -->
      <div class="contact-hero-inner">
        <div class="hero-copy">
          <h1>{{ t('contact.title') }}</h1>
          <p>{{ t('contact.subtitle') }}</p>
        </div>
        <div class="hero-visual">
          <img class="hero-logo" :src="logoUrl" :alt="t('common.brandLogoAlt')" />
        </div>
      </div>
    </section>

    <section class="contact-body">
      <div class="info-card">
        <h2>{{ t('contact.infoTitle') }}</h2>
        <div class="info-item">
          <strong>{{ t('contact.officeLabel') }}</strong>
          <p>{{ t('contact.officeAddress') }}</p>
        </div>
        <div class="info-item">
          <strong>{{ t('common.email') }}</strong>
          <p>
            <a href="mailto:2017emilyli@gmail.com">2017emilyli@gmail.com</a>
          </p>
        </div>
        <div class="info-item">
          <strong>{{ t('contact.whatsappLabel') }}</strong>
          <p>
            <a href="https://wa.me/8618666080334" target="_blank" rel="noopener noreferrer">
              18666080334
            </a>
          </p>
        </div>
        <div class="info-item">
          <strong>{{ t('contact.workHoursLabel') }}</strong>
          <p>{{ t('contact.workHours') }}</p>
        </div>
      </div>

      <form class="contact-form" @submit.prevent="submitForm">
        <h2>{{ t('contact.formTitle') }}</h2>
        <p class="form-tip">{{ t('contact.formTip') }}</p>

        <div class="form-grid">
          <label>
            <span>{{ t('contact.name') }}</span>
            <input
              v-model.trim="form.name"
              type="text"
              required
              :placeholder="t('contact.namePlaceholder')"
            />
          </label>
          <label>
            <span>{{ t('contact.company') }}</span>
            <input
              v-model.trim="form.company"
              type="text"
              :placeholder="t('contact.companyPlaceholder')"
            />
          </label>
          <label>
            <span>{{ t('contact.emailLabel') }}</span>
            <input
              v-model.trim="form.email"
              type="email"
              required
              placeholder="name@company.com"
            />
          </label>
          <label>
            <span>{{ t('contact.phoneLabel') }}</span>
            <input
              v-model.trim="form.phone"
              type="tel"
              :placeholder="t('contact.phonePlaceholder')"
            />
          </label>
        </div>

        <label class="full">
          <span>{{ t('contact.help') }}</span>
          <textarea
            v-model.trim="form.message"
            required
            rows="5"
            :placeholder="t('contact.helpPlaceholder')"
          ></textarea>
        </label>

        <label class="checkbox">
          <input v-model="form.wantCall" type="checkbox" />
          <span>{{ t('contact.wantCall') }}</span>
        </label>

        <button class="submit-btn" type="submit" :disabled="submitting">
          {{ submitting ? t('contact.submitting') : t('contact.submit') }}
        </button>

        <p v-if="successMessage" class="success">{{ successMessage }}</p>
        <p v-if="errorMessage" class="error">{{ errorMessage }}</p>
      </form>
    </section>
  </div>
</template>

<script setup>
import { reactive, ref } from 'vue'
import { useI18n } from 'vue-i18n'
import { submitInquiry } from '@/services/api'
import logoUrl from '@/assets/logo.png'

const { t } = useI18n()

/**
 * 联系表单状态
 * 用途：收集用户询盘信息，提交至后端发信
 */
const form = reactive({
  name: '',
  company: '',
  email: '',
  phone: '',
  message: '',
  wantCall: false
})

const submitting = ref(false)
const successMessage = ref('')
const errorMessage = ref('')

/** 简易邮箱校验，避免明显错误提交 */
const isValidEmail = (email) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)

/**
 * 提交询盘
 * 用法：表单 @submit.prevent 触发；成功后清空表单并展示提示
 */
const submitForm = async () => {
  successMessage.value = ''
  errorMessage.value = ''

  if (!form.name || !form.email || !form.message) {
    errorMessage.value = t('contact.errorRequired')
    return
  }

  if (!isValidEmail(form.email)) {
    errorMessage.value = t('contact.errorEmail')
    return
  }

  submitting.value = true
  try {
    await submitInquiry({
      name: form.name,
      company: form.company,
      email: form.email,
      phone: form.phone,
      message: form.message,
      wantCall: form.wantCall
    })
    successMessage.value = t('contact.success')
    form.name = ''
    form.company = ''
    form.email = ''
    form.phone = ''
    form.message = ''
    form.wantCall = false
  } catch (err) {
    errorMessage.value = (err && err.message) || t('contact.fail')
  } finally {
    submitting.value = false
  }
}
</script>

<style scoped>
.contact-page {
  width: 100%;
}

.contact-hero {
  background:
    radial-gradient(circle at top left, rgba(210, 7, 22, 0.14), transparent 40%),
    linear-gradient(180deg, #fff 0%, #fafafa 100%);
  border-bottom: 1px solid var(--color-border);
}

.contact-hero-inner {
  max-width: var(--max-width);
  margin: 0 auto;
  padding: 48px 24px;
  display: grid;
  grid-template-columns: minmax(0, 1.15fr) minmax(180px, 0.85fr);
  align-items: center;
  gap: 32px;
  text-align: left;
}

.hero-copy {
  min-width: 0;
}

.hero-visual {
  display: flex;
  align-items: center;
  justify-content: center;
}

/* 右侧大尺寸 Logo，与首页 Hero 保持一致 */
.hero-logo {
  width: clamp(160px, 28vw, 280px);
  height: auto;
  aspect-ratio: 1;
  object-fit: contain;
  display: block;
}

.contact-hero h1 {
  max-width: 640px;
  font-size: clamp(28px, 4vw, 40px);
  font-weight: 700;
  margin-bottom: 12px;
}

.contact-hero p {
  max-width: 520px;
  color: var(--color-text-secondary);
  font-size: 16px;
  line-height: 1.7;
}

.contact-body {
  max-width: var(--max-width);
  margin: 0 auto;
  padding: 40px 24px 72px;
  display: grid;
  grid-template-columns: 0.85fr 1.15fr;
  gap: 28px;
  align-items: start;
}

.info-card,
.contact-form {
  background: #fff;
  border: 1px solid var(--color-border);
  border-radius: 16px;
  padding: 28px;
  text-align: left;
}

.info-card h2,
.contact-form h2 {
  font-size: 22px;
  font-weight: 700;
  margin-bottom: 16px;
}

.info-item {
  margin-bottom: 18px;
}

.info-item strong {
  display: block;
  font-size: 14px;
  font-weight: 600;
  margin-bottom: 4px;
}

.info-item p {
  color: var(--color-text-secondary);
  font-size: 14px;
  line-height: 1.6;
}

.info-item a {
  color: var(--color-primary, #d20716);
  text-decoration: none;
}

.info-item a:hover {
  text-decoration: underline;
}

.form-tip {
  color: var(--color-text-secondary);
  font-size: 14px;
  margin-bottom: 20px;
}

.form-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 14px;
  margin-bottom: 14px;
}

label {
  display: flex;
  flex-direction: column;
  gap: 6px;
  font-size: 13px;
  color: var(--color-text-secondary);
}

label.full {
  margin-bottom: 14px;
}

input,
textarea {
  width: 100%;
  box-sizing: border-box;
  border: 1px solid var(--color-border);
  border-radius: 10px;
  padding: 12px 14px;
  font-size: 14px;
  color: var(--color-text);
  background: #fff;
  transition: border-color 0.2s ease, box-shadow 0.2s ease;
}

input:focus,
textarea:focus {
  border-color: var(--color-primary);
  box-shadow: 0 0 0 3px var(--color-primary-soft);
}

.checkbox {
  flex-direction: row;
  align-items: center;
  gap: 8px;
  margin-bottom: 18px;
  cursor: pointer;
}

.checkbox input {
  width: auto;
}

.submit-btn {
  width: 100%;
  border: none;
  border-radius: 999px;
  padding: 14px 20px;
  background: var(--color-primary);
  color: #fff;
  font-size: 15px;
  font-weight: 600;
  cursor: pointer;
  transition: background 0.2s ease;
}

.submit-btn:hover:not(:disabled) {
  background: var(--color-primary-hover);
}

.submit-btn:disabled {
  opacity: 0.7;
  cursor: not-allowed;
}

.success {
  margin-top: 14px;
  color: #0f7a3f;
  font-size: 14px;
}

.error {
  margin-top: 14px;
  color: var(--color-primary);
  font-size: 14px;
}

@media (max-width: 860px) {
  .contact-body {
    grid-template-columns: 1fr;
    padding: 28px 16px 56px;
  }

  .form-grid {
    grid-template-columns: 1fr;
  }

  .contact-hero-inner {
    grid-template-columns: 1fr;
    gap: 24px;
    padding: 36px 16px;
  }

  .hero-visual {
    order: -1;
    justify-content: flex-start;
  }

  .hero-logo {
    width: clamp(120px, 36vw, 180px);
  }
}
</style>
